import React, { useEffect, useRef, useState } from 'react';
import { Col, ControlLabel, FormGroup, HelpBlock, Row } from 'react-bootstrap';
import { Field } from 'react-final-form';
import FieldGroup from '../../../FieldGroup.jsx';
import { FormError } from '../../../FormError.jsx';
import RatingField from '../RatingField.jsx';
import { UploadIcon } from '../../../icons/UploadIcon.jsx';
import {
  ENGAGEMENT_FREQUENCY,
  SUPPORT_FOR_CHANGE,
} from '../../../../lib/schema.js';
import OrgStakeholdersSelect from './StakeholderFormFields/OrgStakeholdersSelect.jsx';
import MultistepFormsFooter from '../../../MultistepFormsFooter.jsx';
import FormStepsNav from './FormStepsNav.jsx';
import { stakeholdersParentTypeOptions } from '../../../../containers/projects/StakeholderFormContainers/utils/index.js';
import CustomRadio from '../../../CustomRadio.jsx';
import AuthorizedAction from '../../../AuthorizedAction.js';

function StepStakeholder(props) {
  const {
    handleSubmit,
    submitFailed,
    onCancel,
    fields,
    handleImageSelect,
    clearImageField,
    errors,
    loading,
    editing,
    orgStakeholders,
    stakeholderParentType,
    setStakeholderParentType,
    selectedOrgStakeholderId,
    setSelectedOrgStakeholderId,
    onShowAddStakeholderModal,
    stakeholder,
    stakeholderDraft,
    submitError,
    values,
  } = props;

  const [fileName, setFileName] = useState('');

  const inputRef = useRef();
  const file = inputRef.current ? inputRef.current.files[0] : null;

  const {
    memberCount,
    influence,
    interest,
    impact,
    engagementFrequency,
    supportForChange,
  } = fields;

  useEffect(() => {
    if (file) {
      setFileName(file.name);
    }
  }, [file]);

  useEffect(() => {
    values.name = stakeholderDraft ? stakeholderDraft.name : stakeholder.name;
  }, [stakeholderParentType]);

  return (
    <form>
      <Row>
        <Col xs={12}>
          <FormStepsNav activeIndex={0} onSubmit={handleSubmit} />
        </Col>
      </Row>

      <Row className="m-b-12">
        <Col xs={12}>
          <div className="component-sm component-bg-blue component-hide-border component-margin-bottom">
            <h3>Stakeholder Name</h3>
          </div>
        </Col>
        <Col xs={10}>
          <p className="text-muted m-b-24">
            Select Stakeholder from existing Structure or enter Stakeholder Name
            manually
          </p>
          <div className="m-b-18">
            <CustomRadio
              name="parentType"
              checkedValue={stakeholderParentType}
              options={stakeholdersParentTypeOptions}
              action={(value) => {
                setStakeholderParentType(value);
                setSelectedOrgStakeholderId(null);
              }}
            />
          </div>
        </Col>
      </Row>
      <Row>
        {stakeholderParentType === 'existing' ? (
          <Col xs={8}>
            <Field
              id="name"
              name="name"
              orgStakeholders={orgStakeholders}
              selectedOrgStakeholderId={selectedOrgStakeholderId}
              setSelectedOrgStakeholderId={setSelectedOrgStakeholderId}
              onShowAddStakeholderModal={onShowAddStakeholderModal}
              component={OrgStakeholdersSelect}
              addBtn={
                <AuthorizedAction
                  action="create"
                  subject="organizationStakeholder"
                >
                  <a
                    href="#"
                    onClick={(event) => {
                      event.preventDefault();
                      onShowAddStakeholderModal();
                    }}
                  >
                    {' '}
                    + Add Stakeholder
                  </a>
                </AuthorizedAction>
              }
            />
          </Col>
        ) : (
          <Col xs={8}>
            <Field
              id="name"
              name="name"
              label="Name"
              placeholder="Name"
              type="text"
              component={FieldGroup}
            />
          </Col>
        )}
      </Row>

      <Row>
        <Col xs={5}>
          <FormGroup className="flex flex-col flex-align-start">
            <ControlLabel>Upload</ControlLabel>
            <div className="m-b-8 w-100">
              <label htmlFor="avatar" className="file-input">
                <UploadIcon />
                <span className="m-l-4">Upload</span>
                <input
                  style={{ display: 'none' }}
                  ref={inputRef}
                  id="avatar"
                  type="file"
                  accept=".jpeg, .png, .jpg"
                  onChange={handleImageSelect}
                />
              </label>
              <div className="file-name m-t-8">{fileName}</div>
            </div>
            {submitFailed && errors.avatar && (
              <FormGroup validationState="error m-0">
                <HelpBlock>{errors.avatar}</HelpBlock>
              </FormGroup>
            )}
            <p className="m-0 text-muted">Allowed formats: .jpeg, .png, .jpg</p>
            <p className="text-muted">Max file size: 10Mb</p>
          </FormGroup>
        </Col>
        {fileName ? (
          <Col xs={1}>
            <button
              type="button"
              className="btn-delete btn btn-default m-t-28"
              onClick={() => {
                clearImageField();
                setFileName('');
              }}
            ></button>
          </Col>
        ) : null}
      </Row>

      <Row>
        <Col xs={8}>
          <Field
            id={memberCount.key}
            name={memberCount.key}
            label={memberCount.name}
            placeholder="Number of people"
            type="number"
            min="0"
            component={FieldGroup}
          />
        </Col>
      </Row>
      <Row>
        <Col sm={3}>
          <RatingField label={influence.name} name={influence.key} />
        </Col>
        <Col sm={3}>
          <RatingField label={interest.name} name={interest.key} />
        </Col>
        <Col sm={4}>
          <RatingField label={impact.name} name={impact.key} />
        </Col>
      </Row>
      <Row className="m-b-48">
        <Col xs={4}>
          <Field
            id={supportForChange.key}
            name={supportForChange.key}
            label={supportForChange.name}
            componentClass="select"
            customClass="m-b-12"
            component={FieldGroup}
          >
            <option value="" disabled>
              - Select -
            </option>
            {SUPPORT_FOR_CHANGE.map((type, index) => (
              <option key={index} value={type}>
                {type}
              </option>
            ))}
          </Field>
        </Col>
        <Col xs={4}>
          <Field
            id={engagementFrequency.key}
            name={engagementFrequency.key}
            label={engagementFrequency.name}
            componentClass="select"
            customClass="m-b-12"
            component={FieldGroup}
          >
            <option value="" disabled>
              - Select -
            </option>
            {ENGAGEMENT_FREQUENCY.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </Field>
        </Col>
      </Row>
      <Row>
        <Col xs={12}>
          {submitFailed && submitError ? (
            <FormError submitError={submitError} />
          ) : null}
        </Col>
        <Col xs={12}>
          <MultistepFormsFooter
            values={values}
            loading={loading}
            handleSubmit={handleSubmit}
            onCancel={onCancel}
            disabledSaveAndExit={loading || !editing}
          />
        </Col>
      </Row>
    </form>
  );
}

export default StepStakeholder;
